import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api';
import { Link } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell';
import './QuoteRequestsPage.css';

export default function QuoteRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('new');
  const [sortBy, setSortBy] = useState('date');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    fetchQuoteRequests();
  }, []);

  // Listen for push notification refresh events
  useEffect(() => {
    const handlePushRefresh = (event) => {
      console.log('🔔 Push notification refresh received:', event.detail);
      fetchQuoteRequests(); // Refresh the quotes list
    };

    window.addEventListener('pushNotificationRefresh', handlePushRefresh);
    
    return () => {
      window.removeEventListener('pushNotificationRefresh', handlePushRefresh);
    };
  }, []);
  
  const fetchQuoteRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('api/quote-requests');
      if (response.data?.success && Array.isArray(response.data.requests)) {
        setRequests(response.data.requests);
      } else {
        setError('Unexpected data format received from server');
      }
    } catch {
      setError('Failed to load quote requests. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const searchQuoteRequests = useCallback(async (term) => {
    if (!term.trim()) {
      setSearchResults(null);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await api.get(`/api/quote-requests/search?term=${encodeURIComponent(term)}`);
      if (response.data?.success && Array.isArray(response.data.requests)) {
        setSearchResults(response.data.requests);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchQuoteRequests(searchTerm);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm, searchQuoteRequests]);

  const filteredRequests = useMemo(() => {
    // Use search results if searching, otherwise use all requests
    let filtered = searchResults !== null ? [...searchResults] : [...requests];

    // Filter by tab only if not searching
    if (searchResults === null) {
      if (activeTab === 'new') {
        filtered = filtered.filter(r => ['draft','assigned'].includes(r.state));
      } else if (activeTab === 'in-progress') {
        filtered = filtered.filter(r => r.state === 'in_progress');
      } else if (activeTab === 'sent') {
        filtered = filtered.filter(r => r.state === 'sent');
      } else if (activeTab === 'done') {
        filtered = filtered.filter(r => ['completed','cancelled'].includes(r.state));
      }
    }

    // Sort
    return filtered.sort((a, b) => {
      if (sortBy === 'date')    return new Date(b.date_created||0) - new Date(a.date_created||0);
      if (sortBy === 'name')    return (a.name||'').localeCompare(b.name||'');
      if (sortBy === 'progress')return (b.progress||0) - (a.progress||0);
      return 0;
    });
  }, [searchResults, requests, activeTab, sortBy]);

  // Compute all four tab counts in a single pass instead of one .filter() per tab per render
  const tabCounts = useMemo(() => {
    return requests.reduce((counts, r) => {
      if (['draft','assigned'].includes(r.state)) counts.new++;
      else if (r.state === 'in_progress') counts['in-progress']++;
      else if (r.state === 'sent') counts.sent++;
      else if (['completed','cancelled'].includes(r.state)) counts.done++;
      return counts;
    }, { new: 0, 'in-progress': 0, sent: 0, done: 0 });
  }, [requests]);

  const getStateBadgeClass = state => ({
    draft: 'badge-secondary',
    assigned: 'badge-info',
    in_progress: 'badge-primary',
    sent: 'badge-warning',
    completed: 'badge-success',
    cancelled: 'badge-danger',
  }[state] || 'badge-secondary');

  if (loading) {
    return (
      <div className="loading-container" role="status" aria-live="polite">
        <div className="spinner" aria-hidden="true"></div>
        <span className="sr-only">Loading quote requests…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container" role="alert">
        <h1>Your Quote Requests</h1>
        <p className="sr-only">Error:</p>
        <div className="alert alert-danger">
          <p className="error-message">{error}</p>
          <button 
            className="btn btn-outline-danger" 
            onClick={fetchQuoteRequests}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quote-requests-container">
      <h1>Your Quote Requests</h1>

      {/* Tabs */}
      <nav className="tabs-container" aria-label="Quote request filters">
        <div role="tablist" className="tabs-nav">
          {['new','in-progress','sent','done'].map(tabKey => {
            const labels = { new: 'New', 'in-progress': 'In Progress', done: 'Done', sent: 'Submitted to customer' };
            return (
              <button
                key={tabKey}
                role="tab"
                aria-selected={activeTab === tabKey}
                aria-controls={`panel-${tabKey}`}
                id={`tab-${tabKey}`}
                className={`tab-button ${activeTab===tabKey?'active':''}`}
                onClick={() => setActiveTab(tabKey)}
              >
                {labels[tabKey]} <span className="tab-count">({tabCounts[tabKey]})</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Controls */}
      <div className="controls-row">
        <div className="sort-control">
          <label htmlFor="sort-select">Sort by:</label>
          <select
            id="sort-select"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="form-control"
          >
            <option value="date">Date (newest first)</option>
            <option value="name">Name</option>
            <option value="progress">Progress</option>
          </select>
        </div>

        <div className="search-control">
          <label htmlFor="search-input" className="sr-only">Search quote requests</label>
          <div className="search-box">
            <input
              id="search-input"
              type="text"
              placeholder="Search by quote name, part number, or product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control search-input"
              aria-label="Search quote requests by name, part number, or product"
            />
            {searchLoading && (
              <span className="search-spinner" aria-label="Searching...">⏳</span>
            )}
            {searchTerm && !searchLoading && (
              <button
                onClick={() => setSearchTerm('')}
                className="clear-search"
                aria-label="Clear search"
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="btn-group">
          <button
            className="btn btn-outline-primary refresh-button"
            onClick={fetchQuoteRequests}
            aria-label="Refresh quote requests"
            title="Refresh"
          >
            <svg 
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
              <path d="M21 3v5h-5"></path>
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
              <path d="M3 21v-5h5"></path>
            </svg>
            <span className="sr-only">Refresh</span>
          </button>
          <NotificationBell />
          <Link
            to="/settings"
            className="btn btn-outline-primary settings-button"
            aria-label="Settings"
            title="Settings"
          >
            <svg 
              className="settings-icon"
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
            <span className="settings-text">Settings</span>
          </Link>
        </div>
      </div>


      {/* Empty State */}
      {filteredRequests.length === 0 && (
        <div className="empty-tab-message" role="status" aria-live="polite">
          {searchTerm ? (
            <>
              <p>No quote requests found matching "{searchTerm}"</p>
              <button
                className="btn btn-outline-primary"
                onClick={() => setSearchTerm('')}
              >
                Clear Search
              </button>
            </>
          ) : (
            <>
              <p>No quote requests found in this tab.</p>
              {activeTab !== 'new' && (
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setActiveTab('new')}
                >
                  View New Requests
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Cards */}
      <div className="quote-requests-grid">
        {filteredRequests.map(req => (
          <Link 
            to={`/quotes/${req.id}`}
            key={req.id}
            className="quote-request-card"
            aria-labelledby={`card-title-${req.id}`}
          >
            <article role="group">
              <header className="card-header">
                <h3 id={`card-title-${req.id}`} className="card-title">
                  {req.name || `#${req.id}`}
                </h3>
                <span 
                  className={`state-badge badge ${getStateBadgeClass(req.state)}`}
                >
                  {req.state_display || req.state}
                </span>
              </header>

              <div className="card-body">
                {req.date_created && (
                  <div className="card-field">
                    <strong>Created:</strong> <span>{req.date_created}</span>
                  </div>
                )}
                {req.date_deadline && (
                  <div className="card-field">
                    <strong>Deadline:</strong> <span>{req.date_deadline}</span>
                  </div>
                )}
                {(req.line_count != null || req.open_line_count != null) && (
                  <div className="card-field">
                    <strong>Items:</strong>{' '}
                    <span>
                      {req.open_line_count != null
                        ? `${req.open_line_count} open / ${req.line_count} total`
                        : `${req.line_count} total`}
                    </span>
                  </div>
                )}
                {req.progress != null && (
                  <div className="progress-container">
                    <div 
                      className="progress"
                      role="progressbar"
                      aria-valuenow={Number(req.progress)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div 
                        className="progress-bar"
                        style={{ 
                          width: `${Math.min(Number(req.progress), 100)}%`,
                          display: 'block'
                        }}
                      >
                        {Math.round(Number(req.progress))}%
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <footer className="card-footer">
                <span className="view-details sr-only">
                  View details for {req.name || `request #${req.id}`}
                </span>
                <span aria-hidden="true">View details →</span>
              </footer>
            </article>
          </Link>
        ))}
      </div>
    </div>
  );
}
